import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { Cell, GameState, MoveHistoryEntry } from '../game/types';
import { copyBoard, wouldCreateLineTooLong, isValidMove, makeMove, hasLegalMoves } from '../game/logic/board';
import { checkForYugos, processYugos } from '../game/logic/yugos';
import { checkForIgo } from '../game/logic/igo';
import { countYugos } from '../game/logic/scoring';
import { INITIAL_BOARD } from '../game/types';
import { playSound, playIllegalMoveSound } from '../utils/sound';
import { ApiService } from '../services/apiService';
import { GameBoard } from './game/GameBoard';
import './Sandbox.css';

// Compare two boards by content (deep equality)
const boardsEqual = (board1: (Cell | null)[][], board2: (Cell | null)[][]): boolean => {
  if (!board1 || !board2) return board1 === board2;
  if (board1.length !== board2.length) return false;
  
  for (let row = 0; row < board1.length; row++) {
    if (board1[row].length !== board2[row].length) return false;
    for (let col = 0; col < board1[row].length; col++) {
      const cell1 = board1[row][col];
      const cell2 = board2[row][col];
      
      if (cell1 === null && cell2 === null) continue;
      if (cell1 === null || cell2 === null) return false;
      if (cell1.color !== cell2.color) return false;
      if (cell1.isYugo !== cell2.isYugo) return false;
    }
  }
  
  return true;
};

// Check if placing a piece would create a line of exactly 4 (which would become a Yugo)
const wouldCreateLineOfFour = (board: (Cell | null)[][], row: number, col: number, playerColor: 'white' | 'black'): boolean => {
  const directions = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1]
  ];

  for (const [dr, dc] of directions) {
    let count = 1;
    
    // Count in positive direction
    let r = row + dr, c = col + dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && 
           board[r][c] && board[r][c]!.color === playerColor) {
      count++;
      r += dr;
      c += dc;
    }
    
    // Count in negative direction
    r = row - dr;
    c = col - dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && 
           board[r][c] && board[r][c]!.color === playerColor) {
      count++;
      r -= dr;
      c -= dc;
    }
    
    if (count === 4) return true;
  }
  
  return false;
};

interface SandboxProps {
  onStartGame: (board: (Cell | null)[][], startingPlayer: 'white' | 'black') => void;
  onBack: () => void;
  onBoardStateChange?: (board: (Cell | null)[][], startingPlayer: 'white' | 'black') => void;
  onMoveRecorded?: (move: { row: number; col: number; player: 'white' | 'black' }) => void;
  isRecordingSolution?: boolean;
  initialBoard?: (Cell | null)[][];
  initialStartingPlayer?: 'white' | 'black';
  showEngineToggle?: boolean;
  authState?: {
    isAuthenticated: boolean;
    isGuest?: boolean;
    user: {
      id: string;
      username: string;
      email: string;
    } | null;
  };
}

const getEngineApiUrl = () => {
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3002';
  return process.env.REACT_APP_API_URL || 'https://api.migoyugo.com';
};

type PieceType = 'white-migo' | 'white-yugo' | 'black-migo' | 'black-yugo' | null;

export const Sandbox: React.FC<SandboxProps> = ({ 
  onStartGame, 
  onBack, 
  onBoardStateChange, 
  onMoveRecorded, 
  isRecordingSolution = false,
  initialBoard,
  initialStartingPlayer,
  showEngineToggle = false,
  authState
}) => {
  // Setup mode state
  const [setupBoard, setSetupBoard] = useState<(Cell | null)[][]>(
    initialBoard ? copyBoard(initialBoard) : copyBoard(INITIAL_BOARD)
  );
  const [selectedPiece, setSelectedPiece] = useState<PieceType>(null);
  const [startingPlayer, setStartingPlayer] = useState<'white' | 'black'>(
    initialStartingPlayer || 'white'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Game sounds preference (synced with main app via localStorage)
  const [gameSoundsEnabled, setGameSoundsEnabled] = useState(true);
  useEffect(() => {
    const saved = localStorage.getItem('migoyugoGameSoundsEnabled');
    const legacy = localStorage.getItem('migoyugoSoundEnabled');
    if (saved !== null) {
      setGameSoundsEnabled(JSON.parse(saved));
    } else if (legacy !== null) {
      setGameSoundsEnabled(JSON.parse(legacy));
    }
  }, []);
  
  // Update starting player when initialStartingPlayer prop changes
  React.useEffect(() => {
    if (initialStartingPlayer !== undefined) {
      setStartingPlayer(initialStartingPlayer);
    }
  }, [initialStartingPlayer]);
  
  // Game mode state
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [moveHistory, setMoveHistory] = useState<MoveHistoryEntry[]>([]);
  const [historyHoverIndex, setHistoryHoverIndex] = useState<number | null>(null);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [boardHistory, setBoardHistory] = useState<(Cell | null)[][][]>([]);
  const [isReviewMode, setIsReviewMode] = useState(false);

  // Engine (Play vs AI) state
  const [engineEnabled, setEngineEnabled] = useState(false);
  const [engineColor, setEngineColor] = useState<'white' | 'black'>('black');
  const [engineThinking, setEngineThinking] = useState(false);
  
  // Update board when initialBoard prop changes (moved after state declarations)
  // Only update if initialBoard actually changed and we're not loading a saved position
  React.useEffect(() => {
    console.log('[initialBoard Effect] Running - initialBoard:', initialBoard ? 'defined' : 'undefined', 
      'isLoadingSavedPosition:', isLoadingSavedPositionRef.current,
      'isInternalUpdate:', isInternalBoardUpdateRef.current,
      'hasChanged:', initialBoard !== previousInitialBoardRef.current);
    
    // Skip if we're currently loading a saved position
    if (isLoadingSavedPositionRef.current) {
      console.log('[initialBoard Effect] Skipping - loading saved position');
      return;
    }
    
    // Skip if this is an internal board update (user placed a piece, etc.)
    if (isInternalBoardUpdateRef.current) {
      console.log('[initialBoard Effect] Skipping - internal board update');
      // Update the ref to match the new initialBoard so we don't trigger again
      previousInitialBoardRef.current = initialBoard;
      // Reset the flag after a delay to allow React to process all updates
      setTimeout(() => {
        isInternalBoardUpdateRef.current = false;
      }, 100);
      return;
    }
    
    // Compare by content, not just reference
    const hasChanged = initialBoard !== previousInitialBoardRef.current;
    const contentChanged = initialBoard && previousInitialBoardRef.current 
      ? !boardsEqual(initialBoard, previousInitialBoardRef.current)
      : hasChanged;
    
    if (initialBoard && contentChanged) {
      // Also check if the content is different from current setupBoard
      // If it's the same content, don't reset (this prevents loops when parent updates with same content)
      if (boardsEqual(initialBoard, setupBoard)) {
        console.log('[initialBoard Effect] Skipping - initialBoard has same content as current setupBoard');
        previousInitialBoardRef.current = initialBoard;
        return;
      }
      
      console.log('[initialBoard Effect] Setting board from initialBoard prop');
      setSetupBoard(copyBoard(initialBoard));
      previousInitialBoardRef.current = initialBoard;
      // Reset game state if a game was started
      if (isGameStarted) {
        setIsGameStarted(false);
        setGameState(null);
        setMoveHistory([]);
        setHistoryIndex(-1);
        setBoardHistory([]);
        setIsReviewMode(false);
      }
    } else if (!initialBoard && previousInitialBoardRef.current) {
      console.log('[initialBoard Effect] initialBoard became undefined, updating ref only');
      previousInitialBoardRef.current = undefined;
    } else if (initialBoard && !contentChanged) {
      // Content is the same, just update the ref
      previousInitialBoardRef.current = initialBoard;
    }
  }, [initialBoard, isGameStarted, setupBoard]);
  
  // Debug: Track when setupBoard changes
  React.useEffect(() => {
    const pieceCount = setupBoard.flat().filter(cell => cell !== null).length;
    const stackTrace = new Error().stack;
    console.log('[Sandbox] setupBoard changed - piece count:', pieceCount, 'startingPlayer:', startingPlayer);
    if (isLoadingSavedPositionRef.current) {
      console.log('[Sandbox] NOTE: This change occurred while loading a saved position');
    }
    // Log stack trace to see what's calling setSetupBoard
    console.log('[Sandbox] Stack trace:', stackTrace?.split('\n').slice(1, 4).join('\n'));
  }, [setupBoard, startingPlayer]);
  
  // Notify parent of board state changes
  React.useEffect(() => {
    if (onBoardStateChange && !isGameStarted) {
      // Note: isInternalBoardUpdateRef is set to true before setSetupBoard is called
      // in user interaction handlers (handleSetupCellClick, handleClearBoard, etc.)
      // The flag will be reset in the initialBoard effect when it skips due to internal update
      onBoardStateChange(copyBoard(setupBoard), startingPlayer);
    }
  }, [setupBoard, startingPlayer, onBoardStateChange, isGameStarted]);
  const [newlyPlacedDots, setNewlyPlacedDots] = useState<Set<string>>(new Set());
  const [fadingDots, setFadingDots] = useState<Set<string>>(new Set());
  const [currentTheme, setCurrentTheme] = useState('classic');

  // Load theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('migoyugoTheme');
    if (savedTheme && ['classic', 'high-contrast', 'galaxy', 'neon', 'marble', 'wood', 'custom'].includes(savedTheme)) {
      setCurrentTheme(savedTheme);
    }
  }, []);
  const [positionString, setPositionString] = useState<string>('');
  const [showPositionString, setShowPositionString] = useState(false);
  
  // Saved positions state (members only)
  const isMember = authState?.isAuthenticated && !authState?.isGuest;
  const [savedPositions, setSavedPositions] = useState<Array<{ id: number; name: string; position_string: string; created_at: string; updated_at: string }>>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPositionText, setImportPositionText] = useState('');
  const [savePositionName, setSavePositionName] = useState('');
  const [loadingPositions, setLoadingPositions] = useState(false);
  
  // Hold-to-scroll state
  const [holdScrollInterval, setHoldScrollInterval] = useState<NodeJS.Timeout | null>(null);
  const holdStartTimeRef = useRef<number | null>(null);
  const holdScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeReviewMoveRef = useRef<HTMLDivElement | null>(null);
  
  // Track when loading a saved position to prevent initialBoard from overwriting it
  const isLoadingSavedPositionRef = useRef<boolean>(false);
  const previousInitialBoardRef = useRef<(Cell | null)[][] | undefined>(initialBoard);
  // Track when we're updating the board internally to prevent initialBoard effect from resetting it
  const isInternalBoardUpdateRef = useRef<boolean>(false);
  
  // Auto-scroll move history state
  const moveHistoryScrollRef = useRef<HTMLDivElement | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  
  // Load saved positions on mount and when auth state changes
  const loadSavedPositions = useCallback(async () => {
    if (!isMember) return;
    setLoadingPositions(true);
    try {
      const result = await ApiService.getSavedPositions();
      if (result.error) {
        console.error('Failed to load saved positions:', result.error);
      } else {
        setSavedPositions(result.positions);
      }
    } catch (error) {
      console.error('Error loading saved positions:', error);
    } finally {
      setLoadingPositions(false);
    }
  }, [isMember]);
  
  useEffect(() => {
    if (isMember) {
      loadSavedPositions();
    } else {
      setSavedPositions([]);
    }
  }, [isMember, loadSavedPositions]);
  
  const handleSavePosition = async () => {
    if (!isMember || !savePositionName.trim()) return;
    
    // Always generate position string from current board state, don't rely on stale positionString state
    const currentPositionString = isGameStarted && gameState 
      ? boardToPositionString(gameState.board, startingPlayer)
      : boardToPositionString(setupBoard, startingPlayer);
    
    const currentBoard = isGameStarted && gameState ? gameState.board : setupBoard;
    const boardPieceCount = currentBoard.flat().filter(cell => cell !== null).length;
    
    console.log('[Save Position] Saving position:', {
      name: savePositionName.trim(),
      position_string: currentPositionString,
      position_string_length: currentPositionString.length,
      boardPieceCount: boardPieceCount,
      startingPlayer: startingPlayer,
      isGameStarted: isGameStarted
    });
    console.log('[Save Position] Full position_string:', JSON.stringify(currentPositionString));
    
    // Verify the position string matches the current board
    const verificationString = boardToPositionString(currentBoard, startingPlayer);
    if (verificationString !== currentPositionString) {
      console.error('[Save Position] ERROR: Generated position string does not match!', {
        generated: currentPositionString,
        verification: verificationString
      });
    }
    
    try {
      const result = await ApiService.savePosition(savePositionName.trim(), currentPositionString);
      if (result.error) {
        alert(`Failed to save position: ${result.error}`);
      } else {
        setShowSaveModal(false);
        setSavePositionName('');
        await loadSavedPositions();
        alert('Position saved successfully!');
      }
    } catch (error) {
      console.error('Error saving position:', error);
      alert('Failed to save position');
    }
  };
  
  const handleLoadPosition = async (positionId: number) => {
    if (!isMember) return;
    
    // Set flag to prevent initialBoard effect from overwriting
    isLoadingSavedPositionRef.current = true;
    
    try {
      console.log('[Load Position] Loading position with ID:', positionId);
      
      const result = await ApiService.getSavedPositionById(positionId);
      console.log('[Load Position] API response:', result);
      
      if (result.error || !result.position) {
        alert(`Failed to load position: ${result.error || 'Position not found'}`);
        isLoadingSavedPositionRef.current = false;
        return;
      }
      
      console.log('[Load Position] Position data:', {
        id: result.position.id,
        name: result.position.name,
        position_string: result.position.position_string,
        position_string_length: result.position.position_string?.length
      });
      
      // Log the full position string to verify it's different for each position
      console.log('[Load Position] Full position_string:', JSON.stringify(result.position.position_string));
      
      const parsed = positionStringToBoard(result.position.position_string);
      if (!parsed) {
        alert('Invalid position format');
        isLoadingSavedPositionRef.current = false;
        return;
      }
      
      console.log('[Load Position] Parsed board:', {
        startingPlayer: parsed.startingPlayer,
        boardPieceCount: parsed.board.flat().filter(cell => cell !== null).length,
        boardPreview: parsed.board.map((row, r) => 
          row.map((cell, c) => cell ? `${cell.color}-${cell.isYugo ? 'Y' : 'M'}` : null).filter(x => x)
        ).filter(row => row.length > 0)
      });
      
      // Reset game state if started
      if (isGameStarted) {
        setIsGameStarted(false);
        setGameState(null);
        setMoveHistory([]);
        setHistoryIndex(-1);
        setBoardHistory([]);
        setIsReviewMode(false);
      }
      
      // Set the loaded position using functional updates to ensure correct values
      console.log('[Load Position] About to set state with:', {
        boardPieceCount: parsed.board.flat().filter(cell => cell !== null).length,
        startingPlayer: parsed.startingPlayer,
        position_string: result.position.position_string
      });
      
      // Store the loaded data in a ref to verify later
      const loadedBoardRef = { board: parsed.board, startingPlayer: parsed.startingPlayer, positionString: result.position.position_string };
      
      // Use functional updates to ensure we set the exact values we want
      setSetupBoard(() => {
        console.log('[Load Position] setSetupBoard called with board piece count:', 
          parsed.board.flat().filter(cell => cell !== null).length);
        return copyBoard(parsed.board);
      });
      
      setStartingPlayer(() => {
        console.log('[Load Position] setStartingPlayer called with:', parsed.startingPlayer);
        return parsed.startingPlayer;
      });
      
      setPositionString(() => {
        const posString = result.position!.position_string;
        console.log('[Load Position] setPositionString called with:', posString);
        return posString;
      });
      
      // Close the modal after loading
      setShowLoadModal(false);
      
      // Store expected values for verification
      const expectedPieceCount = parsed.board.flat().filter(cell => cell !== null).length;
      
      // Verify state was set correctly after React processes the updates
      setTimeout(() => {
        console.log('[Load Position] Verifying state after update - expected piece count:', expectedPieceCount);
      }, 300);
      
      console.log('[Load Position] State update called successfully');
      
      // Reset flag after a longer delay to ensure all state updates complete
      setTimeout(() => {
        isLoadingSavedPositionRef.current = false;
        console.log('[Load Position] Protection flag reset');
      }, 500);
    } catch (error) {
      console.error('[Load Position] Error loading position:', error);
      alert('Failed to load position');
      isLoadingSavedPositionRef.current = false;
    }
  };
  
  const handleDeletePosition = async (positionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isMember) return;
    
    if (!window.confirm('Are you sure you want to delete this saved position?')) {
      return;
    }
    
    try {
      const result = await ApiService.deleteSavedPosition(positionId);
      if (result.error) {
        alert(`Failed to delete position: ${result.error}`);
      } else {
        await loadSavedPositions();
      }
    } catch (error) {
      console.error('Error deleting position:', error);
      alert('Failed to delete position');
    }
  };
  
  const parseNotation = useCallback((notation: string): { row: number; col: number } | null => {
    if (!notation || notation.length !== 2) return null;
    // The game replay copies history with uppercase files (D3), so fold the case
    // before the charCode math, which is anchored to lowercase 'a'.
    const col = notation.toLowerCase().charCodeAt(0) - 97;
    const row = 8 - parseInt(notation[1]);
    if (col < 0 || col > 7 || row < 0 || row > 7 || isNaN(row)) return null;
    return { row, col };
  }, []);

  const parseMoveHistory = useCallback((text: string): MoveHistoryEntry[] => {
    const moves: MoveHistoryEntry[] = [];
    const cleaned = text.replace(/●/g, '*');
    const lines = cleaned.split('\n').filter(line => line.trim());

    for (const line of lines) {
      const match = line.match(/^\s*(\d+)\.\s*([a-h][1-8])(\*{0,4})?(?:\s*\(\d+\s*yugos?\))?\s*([a-h][1-8])?(\*{0,4})?(?:\s*\(\d+\s*yugos?\))?\s*$/i);
      if (match) {
        const moveNumber = parseInt(match[1], 10);
        const whiteNotation = match[2];
        const whiteYugos = match[3] ? match[3].length : 0;
        const blackNotation = match[4];
        const blackYugos = match[5] ? match[5].length : 0;

        const whiteMove = parseNotation(whiteNotation);
        if (whiteMove) {
          moves.push({ row: whiteMove.row, col: whiteMove.col, player: 'white', yugos: whiteYugos, moveNumber: moveNumber * 2 - 1 });
        }
        if (blackNotation) {
          const blackMove = parseNotation(blackNotation);
          if (blackMove) {
            moves.push({ row: blackMove.row, col: blackMove.col, player: 'black', yugos: blackYugos, moveNumber: moveNumber * 2 });
          }
        }
      }
    }
    return moves;
  }, [parseNotation]);

  const replayMoveHistory = useCallback((moves: MoveHistoryEntry[]): {
    boards: (Cell | null)[][][];
    finalGameState: GameState;
    replayedMoves: MoveHistoryEntry[];
  } | null => {
    const initialBoard = copyBoard(INITIAL_BOARD);
    const boards: (Cell | null)[][][] = [copyBoard(initialBoard)];
    let currentBoard = initialBoard;
    let currentPlayer: 'white' | 'black' = 'white';
    let gameOver = false;
    let igoLine: { row: number; col: number }[] | null = null;
    const replayedMoves: MoveHistoryEntry[] = [];

    for (const move of moves) {
      if (gameOver) break;

      const { row, col } = move;
      if (row < 0 || row > 7 || col < 0 || col > 7) return null;
      if (currentBoard[row][col] !== null) return null;

      const newBoard = copyBoard(currentBoard);
      newBoard[row][col] = { color: currentPlayer, isYugo: false };

      const yugos = checkForYugos(newBoard, row, col, currentPlayer);
      processYugos(newBoard, yugos, row, col);

      if (yugos.length > 0) {
        newBoard[row][col] = { color: currentPlayer, isYugo: true };
      }

      const igo = checkForIgo(newBoard, row, col, currentPlayer);
      if (igo) {
        gameOver = true;
        igoLine = igo;
      } else {
        const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
        if (!hasLegalMoves(newBoard, nextPlayer)) {
          gameOver = true;
        }
      }

      replayedMoves.push({
        row, col, player: currentPlayer,
        yugos: yugos.length,
        moveNumber: replayedMoves.length + 1
      });

      boards.push(copyBoard(newBoard));
      currentBoard = newBoard;
      if (!gameOver) {
        currentPlayer = currentPlayer === 'white' ? 'black' : 'white';
      }
    }

    const scores = {
      white: countYugos(currentBoard, 'white'),
      black: countYugos(currentBoard, 'black')
    };

    const lastMove = replayedMoves.length > 0
      ? { row: replayedMoves[replayedMoves.length - 1].row, col: replayedMoves[replayedMoves.length - 1].col, player: replayedMoves[replayedMoves.length - 1].player }
      : null;

    return {
      boards,
      finalGameState: {
        board: currentBoard,
        currentPlayer,
        scores,
        gameStatus: gameOver ? 'finished' : 'active',
        lastMove,
        players: { white: 'White', black: 'Black' },
        igoLine
      },
      replayedMoves
    };
  }, []);

  const handleImport = () => {
    const text = importPositionText.trim();
    if (!text) return;

    const isPositionString = text.includes('WM:') || text.includes('WY:') || text.includes('BM:') || text.includes('BY:');

    if (isPositionString) {
      const parsed = positionStringToBoard(text);
      if (!parsed || parsed.board.flat().every(cell => cell === null)) {
        alert('Invalid position string. Expected format:\nWM:a1,b2;WY:c3;BM:d4;BY:e5;START:white');
        return;
      }

      isLoadingSavedPositionRef.current = true;

      if (isGameStarted) {
        setIsGameStarted(false);
        setGameState(null);
        setMoveHistory([]);
        setHistoryIndex(-1);
        setBoardHistory([]);
        setIsReviewMode(false);
      }

      setSetupBoard(() => copyBoard(parsed.board));
      setStartingPlayer(() => parsed.startingPlayer);
      setPositionString(text);

      setShowImportModal(false);
      setImportPositionText('');

      setTimeout(() => {
        isLoadingSavedPositionRef.current = false;
      }, 500);
    } else {
      const parsedMoves = parseMoveHistory(text);
      if (parsedMoves.length === 0) {
        alert('Invalid format. Could not parse as position string or move history.\n\nMove history format:\n1. a1 b2\n2. c3 d4\n\nPosition string format:\nWM:a1,b2;BM:c3;START:white');
        return;
      }

      const result = replayMoveHistory(parsedMoves);
      if (!result) {
        alert('Failed to replay move history. Some moves may be invalid.');
        return;
      }

      isLoadingSavedPositionRef.current = true;

      setGameState(result.finalGameState);
      setMoveHistory(result.replayedMoves);
      setBoardHistory(result.boards);
      setHistoryIndex(result.replayedMoves.length - 1);
      setIsGameStarted(true);
      setIsReviewMode(false);
      setStartingPlayer('white');
      setSelectedPiece(null);
      setErrorMessage(null);

      setShowImportModal(false);
      setImportPositionText('');

      setTimeout(() => {
        isLoadingSavedPositionRef.current = false;
      }, 500);
    }
  };

  // Helper function to convert col/row to notation (e.g., 0,0 -> "a8")
  const getNotation = useCallback((col: number, row: number): string => {
    const colLetter = String.fromCharCode(97 + col); // a-h
    const rowNumber = 8 - row; // 8-1
    return colLetter + rowNumber;
  }, []);

  const handlePieceSelect = (pieceType: PieceType) => {
    if (isGameStarted) return; // Can't change pieces during game
    setSelectedPiece(pieceType);
    setErrorMessage(null);
  };

  const handleSetupCellClick = useCallback((row: number, col: number) => {
    if (isGameStarted) return; // Can't edit board during game
    
    // If clicking on an occupied cell, remove the piece
    if (setupBoard[row][col] !== null) {
      const newBoard = setupBoard.map(r => [...r]);
      newBoard[row][col] = null;
      isInternalBoardUpdateRef.current = true;
      setSetupBoard(newBoard);
      setErrorMessage(null);
      return;
    }

    // If no piece is selected, do nothing
    if (!selectedPiece) {
      setErrorMessage('Please select a piece type first');
      setTimeout(() => setErrorMessage(null), 2000);
      return;
    }

    // Parse piece type
    const [color, type] = selectedPiece.split('-') as ['white' | 'black', 'migo' | 'yugo'];
    const isYugo = type === 'yugo';

    // Create new cell
    const newCell: Cell = {
      color,
      isYugo
    };

    // Check if placing this piece would create a line > 4 or exactly 4
    const tempBoard = setupBoard.map(r => [...r]);
    tempBoard[row][col] = newCell;
    
    if (wouldCreateLineTooLong(tempBoard, row, col, color)) {
      setErrorMessage('Cannot place piece: would create a line longer than 4');
      setTimeout(() => setErrorMessage(null), 2000);
      return;
    }
    
    if (wouldCreateLineOfFour(tempBoard, row, col, color)) {
      setErrorMessage('Cannot place piece: would create a line of exactly 4 (this would become a Yugo in a real game)');
      setTimeout(() => setErrorMessage(null), 2000);
      return;
    }

    // Place the piece
    const newBoard = setupBoard.map(r => [...r]);
    newBoard[row][col] = newCell;
    isInternalBoardUpdateRef.current = true;
    setSetupBoard(newBoard);
    setErrorMessage(null);
  }, [setupBoard, selectedPiece, isGameStarted]);

  const handleGameCellClick = useCallback((row: number, col: number) => {
    if (!isGameStarted || !gameState || isReviewMode) return;
    if (gameState.gameStatus !== 'active') return;
    
    // Check if move is valid
    if (!isValidMove(gameState.board, row, col, gameState.currentPlayer)) {
      setErrorMessage('Illegal move');
      setTimeout(() => setErrorMessage(null), 2000);
      playIllegalMoveSound(gameSoundsEnabled);
      return;
    }
    
    const currentPlayer = gameState.currentPlayer;
    const newBoard = copyBoard(gameState.board);
    
    // Place the migo
    newBoard[row][col] = { color: currentPlayer, isYugo: false };
    
    // Check for yugos
    const yugos = checkForYugos(newBoard, row, col, currentPlayer);
    const { yugoFormed, removedCells } = processYugos(newBoard, yugos, row, col);
    
    // Trigger fade-out animation for removed dots
    if (removedCells.length > 0) {
      const fadeSet = new Set<string>();
      removedCells.forEach(({ row: r, col: c }) => {
        fadeSet.add(`${r}-${c}`);
      });
      setFadingDots(fadeSet);
      setTimeout(() => setFadingDots(new Set()), 500);
    }
    
    // Trigger bounce-in animation for newly placed dot
    setNewlyPlacedDots(new Set([`${row}-${col}`]));
    setTimeout(() => setNewlyPlacedDots(new Set()), 300);
    
    // If yugos were formed, make this cell a yugo
    if (yugoFormed) {
      newBoard[row][col] = { color: currentPlayer, isYugo: true };
    }
    
    // Update scores
    const newScores = {
      white: countYugos(newBoard, 'white'),
      black: countYugos(newBoard, 'black')
    };
    
    // Check for igo (winning condition)
    const igo = checkForIgo(newBoard, row, col, currentPlayer);
    let gameOver = false;
    let winner: 'white' | 'black' | 'draw' | null = null;
    
    // Play appropriate sound
    if (igo) {
      gameOver = true;
      winner = currentPlayer;
      playSound('igo', gameSoundsEnabled);
    } else if (yugoFormed) {
      playSound('yugo', gameSoundsEnabled);
    } else {
      playSound('migo', gameSoundsEnabled);
    }
    
    if (!igo) {
      // Check if next player has legal moves
      const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
      if (!hasLegalMoves(newBoard, nextPlayer)) {
        gameOver = true;
        if (newScores.white > newScores.black) winner = 'white';
        else if (newScores.black > newScores.white) winner = 'black';
        else winner = 'draw';
        playSound('wego', gameSoundsEnabled);
      }
    }
    
    // Add to move history
    const newMoveHistory = [
      ...moveHistory,
      {
        row,
        col,
        player: currentPlayer,
        yugos: yugos.length,
        moveNumber: moveHistory.length + 1
      }
    ];
    setMoveHistory(newMoveHistory);
    
    // Record move if recording solution
    if (isRecordingSolution && onMoveRecorded) {
      onMoveRecorded({ row, col, player: currentPlayer });
    }
    
    // Store board state after this move in boardHistory
    setBoardHistory(prev => [...prev, copyBoard(newBoard)]);
    
    // Update history index to point to the latest move
    setHistoryIndex(newMoveHistory.length - 1);
    
    // Update game state
    setGameState({
      board: newBoard,
      currentPlayer: gameOver ? currentPlayer : (currentPlayer === 'white' ? 'black' : 'white'),
      scores: newScores,
      gameStatus: gameOver ? 'finished' : 'active',
      lastMove: { row, col, player: currentPlayer },
      players: { white: 'White', black: 'Black' },
      igoLine: igo || null
    });
    
    setErrorMessage(null);
  }, [isGameStarted, gameState, isReviewMode, moveHistory, gameSoundsEnabled, isRecordingSolution, onMoveRecorded]);

  // Wrapper function for cell clicks that routes to the correct handler
  const handleCellClick = useCallback((row: number, col: number) => {
    if (isGameStarted && gameState) {
      if (engineEnabled && gameState.currentPlayer === engineColor) return;
      handleGameCellClick(row, col);
    } else {
      handleSetupCellClick(row, col);
    }
  }, [isGameStarted, gameState, handleGameCellClick, handleSetupCellClick, engineEnabled, engineColor]);

  // Engine move effect: when it's the engine's turn, fetch a move and play it
  useEffect(() => {
    if (!engineEnabled || !isGameStarted || !gameState || gameState.gameStatus !== 'active' || isReviewMode) return;
    if (gameState.currentPlayer !== engineColor) return;
    if (engineThinking) return;

    let cancelled = false;
    setEngineThinking(true);

    const fetchAndApply = async () => {
      try {
        const apiUrl = getEngineApiUrl();
        const response = await fetch(`${apiUrl}/api/engine/move`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            board: gameState.board,
            player_color: engineColor,
            time_limit: 10000,
          }),
        });
        if (cancelled) return;
        if (!response.ok) throw new Error(`Engine ${response.status}`);
        const result = await response.json();
        if (cancelled) return;

        if (result.row !== undefined && result.col !== undefined &&
            isValidMove(gameState.board, result.row, result.col, engineColor)) {
          handleGameCellClick(result.row, result.col);
        }
      } catch (err) {
        if (!cancelled) console.error('[Sandbox Engine] Error:', err);
      } finally {
        if (!cancelled) setEngineThinking(false);
      }
    };

    fetchAndApply();
    return () => { cancelled = true; };
  }, [engineEnabled, isGameStarted, gameState, engineColor, isReviewMode, engineThinking, handleGameCellClick]);

  // Create a GameState-like object for setup mode
  const getDisplayGameState = useCallback((): GameState => {
    if (isGameStarted && gameState) {
      return gameState;
    }
    // Create a minimal GameState for setup mode
    return {
      board: setupBoard,
      currentPlayer: startingPlayer,
      scores: {
        white: countYugos(setupBoard, 'white'),
        black: countYugos(setupBoard, 'black')
      },
      gameStatus: 'active',
      lastMove: null,
      players: { white: 'White', black: 'Black' }
    };
  }, [isGameStarted, gameState, setupBoard, startingPlayer]);

  const handleClearBoard = () => {
    if (isGameStarted) {
      if (window.confirm('Reset to setup mode? This will clear the current game.')) {
        setIsGameStarted(false);
        setGameState(null);
        setMoveHistory([]);
        setHistoryIndex(-1);
        setBoardHistory([]);
        setIsReviewMode(false);
        isInternalBoardUpdateRef.current = true;
        setSetupBoard(copyBoard(INITIAL_BOARD));
        setSelectedPiece(null);
        setErrorMessage(null);
        setPositionString(''); // Clear position string when clearing board
      }
    } else {
      if (window.confirm('Clear the entire board?')) {
        isInternalBoardUpdateRef.current = true;
        setSetupBoard(copyBoard(INITIAL_BOARD));
        setErrorMessage(null);
        setSelectedPiece(null);
        setPositionString(''); // Clear position string when clearing board
      }
    }
  };

  const handleStartGame = () => {
    // Count pieces to ensure board isn't empty
    let pieceCount = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (setupBoard[row][col] !== null) pieceCount++;
      }
    }

    if (pieceCount === 0) {
      setErrorMessage('Please place at least one piece before starting the game');
      setTimeout(() => setErrorMessage(null), 2000);
      return;
    }

    // Calculate initial scores from Yugos on the board
    let whiteScore = 0;
    let blackScore = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const cell = setupBoard[row][col];
        if (cell && cell.isYugo) {
          if (cell.color === 'white') whiteScore++;
          else blackScore++;
        }
      }
    }

    // Initialize game state
    const initialBoard = copyBoard(setupBoard);
    const newGameState: GameState = {
      board: initialBoard,
      currentPlayer: startingPlayer,
      scores: { white: whiteScore, black: blackScore },
      gameStatus: 'active',
      lastMove: null,
      players: { white: 'White', black: 'Black' },
      igoLine: null
    };

    setGameState(newGameState);
    setMoveHistory([]);
    setHistoryIndex(-1);
    setBoardHistory([copyBoard(initialBoard)]);
    setIsGameStarted(true);
    setIsReviewMode(false);
    setSelectedPiece(null);
    setErrorMessage(null);
  };

  // Convert board position to human-readable string format
  // Format: WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white
  const boardToPositionString = useCallback((board: (Cell | null)[][], startPlayer: 'white' | 'black'): string => {
    const whiteMigos: string[] = [];
    const whiteYugos: string[] = [];
    const blackMigos: string[] = [];
    const blackYugos: string[] = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const cell = board[row][col];
        if (cell) {
          const square = `${String.fromCharCode(97 + col)}${8 - row}`;
          if (cell.color === 'white') {
            if (cell.isYugo) {
              whiteYugos.push(square);
            } else {
              whiteMigos.push(square);
            }
          } else {
            if (cell.isYugo) {
              blackYugos.push(square);
            } else {
              blackMigos.push(square);
            }
          }
        }
      }
    }

    const parts: string[] = [];
    if (whiteMigos.length > 0) parts.push(`WM:${whiteMigos.join(',')}`);
    if (whiteYugos.length > 0) parts.push(`WY:${whiteYugos.join(',')}`);
    if (blackMigos.length > 0) parts.push(`BM:${blackMigos.join(',')}`);
    if (blackYugos.length > 0) parts.push(`BY:${blackYugos.join(',')}`);
    parts.push(`START:${startPlayer}`);

    return parts.join(';');
  }, []);

  // Parse position string back to board and starting player
  const positionStringToBoard = useCallback((positionString: string): { board: (Cell | null)[][], startingPlayer: 'white' | 'black' } => {
    const board: (Cell | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));
    let startingPlayer: 'white' | 'black' = 'white';

    const parts = positionString.split(';');
    for (const part of parts) {
      if (part.startsWith('START:')) {
        startingPlayer = part.split(':')[1] as 'white' | 'black';
        continue;
      }

      const [type, squares] = part.split(':');
      if (!squares) continue;

      const squareList = squares.split(',');
      for (const square of squareList) {
        if (square.length !== 2) continue;
        const col = square.charCodeAt(0) - 97;
        const row = 8 - parseInt(square[1]);
        if (row >= 0 && row < 8 && col >= 0 && col < 8) {
          if (type === 'WM') {
            board[row][col] = { color: 'white', isYugo: false };
          } else if (type === 'WY') {
            board[row][col] = { color: 'white', isYugo: true };
          } else if (type === 'BM') {
            board[row][col] = { color: 'black', isYugo: false };
          } else if (type === 'BY') {
            board[row][col] = { color: 'black', isYugo: true };
          }
        }
      }
    }

    return { board, startingPlayer };
  }, []);

  const handleSaveAndGoToGameBoard = () => {
    if (!isGameStarted || !gameState) {
      setErrorMessage('Please start a game first');
      setTimeout(() => setErrorMessage(null), 2000);
      return;
    }

    // Save the current board position and starting player
    const savedPosition = {
      board: copyBoard(gameState.board),
      startingPlayer: startingPlayer,
      currentPlayer: gameState.currentPlayer
    };
    localStorage.setItem('sandboxSavedPosition', JSON.stringify(savedPosition));
    
    // Also generate and save the human-readable position string
    const posString = boardToPositionString(gameState.board, startingPlayer);
    localStorage.setItem('sandboxSavedPositionString', posString);
    setPositionString(posString);
    setShowPositionString(true);
    
    // Navigate to game board
    onStartGame(copyBoard(gameState.board), startingPlayer);
  };

  const handleCopyPositionString = useCallback(async () => {
    if (!positionString) {
      const posString = isGameStarted && gameState 
        ? boardToPositionString(gameState.board, startingPlayer)
        : boardToPositionString(setupBoard, startingPlayer);
      setPositionString(posString);
    }
    
    const stringToCopy = positionString || (isGameStarted && gameState 
      ? boardToPositionString(gameState.board, startingPlayer)
      : boardToPositionString(setupBoard, startingPlayer));
    
    try {
      await navigator.clipboard.writeText(stringToCopy);
      setErrorMessage('Position copied to clipboard!');
      setTimeout(() => setErrorMessage(null), 2000);
    } catch (err) {
      setErrorMessage('Failed to copy position');
      setTimeout(() => setErrorMessage(null), 2000);
    }
  }, [positionString, isGameStarted, gameState, startingPlayer, setupBoard, boardToPositionString]);

  const handleUndoMove = useCallback(() => {
    if (!isGameStarted || !gameState || moveHistory.length === 0) return;
    if (isReviewMode) {
      // If in review mode, just go back to setup position
      setIsReviewMode(false);
      setHistoryIndex(-1);
      if (boardHistory.length > 0) {
        const initialBoard = boardHistory[0];
        const scores = {
          white: countYugos(initialBoard, 'white'),
          black: countYugos(initialBoard, 'black')
        };
        setGameState({
          ...gameState,
          board: initialBoard,
          scores,
          lastMove: null,
          currentPlayer: startingPlayer,
          gameStatus: 'active'
        });
      }
      return;
    }

    // Remove the last move
    const newMoveHistory = moveHistory.slice(0, -1);
    const newBoardHistory = boardHistory.slice(0, -1);
    
    setMoveHistory(newMoveHistory);
    setBoardHistory(newBoardHistory);
    
    // Update history index
    const newIndex = newMoveHistory.length - 1;
    setHistoryIndex(newIndex);
    
    // Restore board state
    if (newBoardHistory.length > 0) {
      const previousBoard = newBoardHistory[newBoardHistory.length - 1];
      const scores = {
        white: countYugos(previousBoard, 'white'),
        black: countYugos(previousBoard, 'black')
      };
      
      // The next move after undo should be the same color as the move just removed
      const removedMove = moveHistory[moveHistory.length - 1];
      let currentPlayer: 'white' | 'black';
      if (removedMove) {
        currentPlayer = removedMove.player;
      } else {
        currentPlayer = startingPlayer;
      }
      
      setGameState({
        ...gameState,
        board: previousBoard,
        scores,
        lastMove: newMoveHistory.length > 0 ? {
          row: newMoveHistory[newMoveHistory.length - 1].row,
          col: newMoveHistory[newMoveHistory.length - 1].col,
          player: newMoveHistory[newMoveHistory.length - 1].player
        } : null,
        currentPlayer,
        gameStatus: 'active'
      });
    } else {
      // Back to setup position
      const initialBoard = boardHistory[0];
      const scores = {
        white: countYugos(initialBoard, 'white'),
        black: countYugos(initialBoard, 'black')
      };
      setGameState({
        ...gameState,
        board: initialBoard,
        scores,
        lastMove: null,
        currentPlayer: startingPlayer,
        gameStatus: 'active'
      });
    }
  }, [isGameStarted, gameState, moveHistory, boardHistory, startingPlayer, isReviewMode]);

  const goToMove = useCallback((moveIndex: number) => {
    if (moveIndex < -1 || moveIndex > moveHistory.length) return;
    
    setIsReviewMode(true);
    setHistoryIndex(moveIndex);
    
    if (moveIndex === -1) {
      // Show initial position (before any moves)
      if (gameState && boardHistory.length > 0) {
        const initialBoard = boardHistory[0];
        const scores = {
          white: countYugos(initialBoard, 'white'),
          black: countYugos(initialBoard, 'black')
        };
        setGameState({
          ...gameState,
          board: initialBoard,
          scores,
          lastMove: null,
          currentPlayer: startingPlayer
        });
      }
    } else if (moveIndex === moveHistory.length) {
      // Show final position (current position)
      if (gameState && boardHistory.length > 0) {
        const finalBoard = boardHistory[boardHistory.length - 1];
        const scores = {
          white: countYugos(finalBoard, 'white'),
          black: countYugos(finalBoard, 'black')
        };
        const lastMove = moveHistory.length > 0 ? {
          row: moveHistory[moveHistory.length - 1].row,
          col: moveHistory[moveHistory.length - 1].col,
          player: moveHistory[moveHistory.length - 1].player
        } : null;
        setGameState({
          ...gameState,
          board: finalBoard,
          scores,
          lastMove,
          currentPlayer: lastMove ? (lastMove.player === 'white' ? 'black' : 'white') : startingPlayer
        });
      }
    } else {
      // Show position after this move (boardHistory[0] is initial, boardHistory[1] is after move 0, etc.)
      const boardAtMove = boardHistory[moveIndex + 1];
      const move = moveHistory[moveIndex];
      
      if (gameState && boardAtMove) {
        // Recalculate scores for this position
        const scores = {
          white: countYugos(boardAtMove, 'white'),
          black: countYugos(boardAtMove, 'black')
        };
        
        setGameState({
          ...gameState,
          board: boardAtMove,
          scores,
          lastMove: move ? { row: move.row, col: move.col, player: move.player } : null,
          currentPlayer: move ? (move.player === 'white' ? 'black' : 'white') : startingPlayer
        });
      }
    }
  }, [boardHistory, moveHistory, gameState, startingPlayer]);
  
  const firstMove = useCallback(() => {
    goToMove(-1);
  }, [goToMove]);
  
  const lastMove = useCallback(() => {
    goToMove(moveHistory.length);
  }, [goToMove, moveHistory.length]);

  const previousMove = useCallback(() => {
    if (historyIndex > -1) {
      goToMove(historyIndex - 1);
    }
  }, [historyIndex, goToMove]);

  const nextMove = useCallback(() => {
    if (historyIndex < moveHistory.length) {
      goToMove(historyIndex + 1);
    }
  }, [historyIndex, moveHistory.length, goToMove]);
  
  const enterReviewMode = useCallback(() => {
    if (moveHistory.length === 0) return;
    setIsReviewMode(true);
    // Start at the last move (final position)
    goToMove(moveHistory.length);
  }, [moveHistory.length, goToMove]);
  
  const exitReviewMode = useCallback(() => {
    setIsReviewMode(false);
    setHistoryIndex(moveHistory.length - 1);
    if (gameState && boardHistory.length > 0) {
      const finalBoard = boardHistory[boardHistory.length - 1];
      const scores = {
        white: countYugos(finalBoard, 'white'),
        black: countYugos(finalBoard, 'black')
      };
      const lastMove = moveHistory.length > 0 ? {
        row: moveHistory[moveHistory.length - 1].row,
        col: moveHistory[moveHistory.length - 1].col,
        player: moveHistory[moveHistory.length - 1].player
      } : null;
      setGameState({
        ...gameState,
        board: finalBoard,
        scores,
        lastMove,
        currentPlayer: lastMove ? (lastMove.player === 'white' ? 'black' : 'white') : startingPlayer
      });
    }
    // Reset auto-scroll when exiting review mode
    setShouldAutoScroll(true);
  }, [moveHistory, gameState, boardHistory, startingPlayer]);
  
  // Check if user is near bottom of scroll container
  const checkIfAtBottom = useCallback(() => {
    if (moveHistoryScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = moveHistoryScrollRef.current;
      const threshold = 50; // pixels from bottom
      const isAtBottom = scrollHeight - scrollTop - clientHeight < threshold;
      setShouldAutoScroll(isAtBottom);
    }
  }, []);
  
  // Handle scroll events to detect if user scrolled up
  const handleMoveHistoryScroll = useCallback(() => {
    checkIfAtBottom();
  }, [checkIfAtBottom]);
  
  // Auto-scroll to bottom when new moves are added (only if at bottom and not in review mode)
  useEffect(() => {
    if (shouldAutoScroll && !isReviewMode && moveHistoryScrollRef.current && moveHistory.length > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (moveHistoryScrollRef.current) {
          moveHistoryScrollRef.current.scrollTop = moveHistoryScrollRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [moveHistory.length, shouldAutoScroll, isReviewMode]);
  
  // Initial scroll to bottom when game starts
  useEffect(() => {
    if (isGameStarted && moveHistoryScrollRef.current && moveHistory.length > 0) {
      setTimeout(() => {
        if (moveHistoryScrollRef.current) {
          moveHistoryScrollRef.current.scrollTop = moveHistoryScrollRef.current.scrollHeight;
          setShouldAutoScroll(true);
        }
      }, 0);
    }
  }, [isGameStarted, moveHistory.length]);
  
  // Reset auto-scroll when exiting review mode
  useEffect(() => {
    if (!isReviewMode) {
      setShouldAutoScroll(true);
    }
  }, [isReviewMode]);
  
  // Hold-to-scroll functionality
  const startHoldScroll = useCallback((direction: 'prev' | 'next', event?: React.MouseEvent | React.TouchEvent) => {
    event?.preventDefault();
    
    // Clear any existing timeouts/intervals
    if (holdScrollInterval) {
      clearTimeout(holdScrollInterval);
      clearInterval(holdScrollInterval);
      setHoldScrollInterval(null);
    }
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    
    // Record start time
    holdStartTimeRef.current = Date.now();
    
    const scrollFunction = direction === 'prev' ? previousMove : nextMove;
    
    // Wait 0.5 seconds before executing first move and starting continuous scroll
    holdScrollTimeoutRef.current = setTimeout(() => {
      // Execute first move after 0.5 seconds
      scrollFunction();
      
      // Then continue scrolling every 333ms (3 moves per second)
      const interval = setInterval(() => {
        scrollFunction();
      }, 333);
      
      setHoldScrollInterval(interval);
    }, 500);
  }, [previousMove, nextMove, holdScrollInterval]);
  
  const stopHoldScroll = useCallback((direction: 'prev' | 'next', event?: React.MouseEvent | React.TouchEvent) => {
    event?.preventDefault();
    
    const scrollFunction = direction === 'prev' ? previousMove : nextMove;
    
    // Check if this was a quick click (< 0.5 seconds)
    if (holdStartTimeRef.current !== null) {
      const holdDuration = Date.now() - holdStartTimeRef.current;
      
      if (holdDuration < 500) {
        // Quick click: execute ONE move
        if (holdScrollTimeoutRef.current) {
          clearTimeout(holdScrollTimeoutRef.current);
          holdScrollTimeoutRef.current = null;
        }
        scrollFunction();
      }
      
      holdStartTimeRef.current = null;
    }
    
    // Clear any existing timeout or interval
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    if (holdScrollInterval) {
      clearInterval(holdScrollInterval);
      setHoldScrollInterval(null);
    }
  }, [previousMove, nextMove, holdScrollInterval]);
  
  // Cleanup hold timeout and interval when component unmounts
  React.useEffect(() => {
    return () => {
      if (holdScrollInterval) {
        clearTimeout(holdScrollInterval);
        clearInterval(holdScrollInterval);
      }
      if (holdScrollTimeoutRef.current) {
        clearTimeout(holdScrollTimeoutRef.current);
      }
    };
  }, [holdScrollInterval]);
  
  // Auto-scroll active move into view in review mode
  React.useEffect(() => {
    if (isReviewMode && activeReviewMoveRef.current) {
      activeReviewMoveRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [historyIndex, isReviewMode]);

  const pieces: ('white-migo' | 'white-yugo' | 'black-migo' | 'black-yugo')[] = ['white-migo', 'white-yugo', 'black-migo', 'black-yugo'];
  const currentBoard = isGameStarted && gameState ? gameState.board : setupBoard;
  
  // Determine player info for top and bottom
  const topPlayerColor = 'white';
  const bottomPlayerColor = 'black';
  const topScore = isGameStarted && gameState ? gameState.scores.white : 0;
  const bottomScore = isGameStarted && gameState ? gameState.scores.black : 0;
  const topIsActive = isGameStarted && gameState && gameState.currentPlayer === 'white';
  const bottomIsActive = isGameStarted && gameState && gameState.currentPlayer === 'black';

  return (
    <div className="sandbox-container">
      <div className="game-container" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', height: '100%', width: '100%', overflow: 'hidden' }}>
        {/* Instructions Panel - LEFT */}
        <div className="sandbox-instructions" style={{ flex: '0 0 300px' }}>
          <h3>Instructions</h3>
          <ul>
            <li>Click on a piece type to select it</li>
            <li>Click on board cells to place pieces</li>
            <li>Click on a placed piece to remove it</li>
            <li>Only legal positions are allowed (no lines longer than 4)</li>
            <li>Lines of exactly 4 are not permitted - in a real game, the 4th piece would immediately become a Yugo, so this position cannot exist</li>
            <li>Select which player moves first</li>
            <li>Click "Start Game" to begin a local game</li>
          </ul>
        </div>

        {/* Game Board Area - CENTER */}
        <div className="game-board-area">
          {/* Top player info */}
          <div className="player-info" style={{ width: 'calc(var(--board-size) + 4px)', boxSizing: 'border-box' }}>
            <div className={`player ${topIsActive ? 'active' : ''}`} id="player-white">
              <div className="player-color white"></div>
              <span>White</span>
              <span>Yugos: <span id="white-score">{topScore}</span></span>
            </div>
          </div>

          {/* Game board */}
          <div className="board-with-labels">
            <div style={{ position: 'relative' }}>
              <GameBoard
                gameState={getDisplayGameState()}
                isReviewMode={isReviewMode}
                newlyPlacedDots={newlyPlacedDots}
                fadingDots={fadingDots}
                onCellClick={handleCellClick}
                currentTheme={currentTheme}
                historyHoverSquare={
                  historyHoverIndex !== null && moveHistory[historyHoverIndex]
                    ? { row: moveHistory[historyHoverIndex].row, col: moveHistory[historyHoverIndex].col }
                    : null
                }
              />
            </div>
          </div>

          {/* Bottom player info */}
          <div className="player-info bottom" style={{ width: 'calc(var(--board-size) + 4px)', boxSizing: 'border-box' }}>
            <div className={`player ${bottomIsActive ? 'active' : ''}`} id="player-black">
              <div className="player-color black"></div>
              <span>Black</span>
              <span>Yugos: <span id="black-score">{bottomScore}</span></span>
            </div>
          </div>
          
          {errorMessage && (
            <div className="sandbox-error" style={{ marginTop: '10px', textAlign: 'center' }}>{errorMessage}</div>
          )}
          {engineEnabled && engineThinking && (
            <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '12px', color: '#FF9800', fontWeight: 500 }}>
              Engine thinking...
            </div>
          )}
        </div>

        {/* Game Controls Area - RIGHT */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {!isGameStarted ? (
            <>
              {/* Pre-game: Piece Selection and Setup Controls */}
              <div className="game-controls-area" style={{ height: 'calc(var(--board-size) + 46px)', width: '256px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div id="pregame-controls">
                  <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Select Piece</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
                    {pieces.map((pieceType) => (
                      <button
                        key={pieceType}
                        className={`sandbox-piece ${pieceType} ${selectedPiece === pieceType ? 'selected' : ''}`}
                        onClick={() => handlePieceSelect(pieceType)}
                        style={{ 
                          padding: '15px',
                          border: `2px solid ${selectedPiece === pieceType ? '#3498db' : '#34495e'}`,
                          borderRadius: '8px',
                          background: selectedPiece === pieceType ? 'rgba(52, 152, 219, 0.2)' : 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <div className="sandbox-piece-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '40px', height: '40px' }}>
                          <div className={`dot ${pieceType.split('-')[0]}`} />
                          {pieceType.includes('yugo') && (
                            <div className={`yugo standard`} />
                          )}
                        </div>
                        <span style={{ fontSize: '0.85rem' }}>
                          {pieceType === 'white-migo' ? 'White Migo' : 
                           pieceType === 'white-yugo' ? 'White Yugo' :
                           pieceType === 'black-migo' ? 'Black Migo' : 'Black Yugo'}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="starting-player-selector" style={{ marginBottom: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-color, #34495e)' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.9rem' }}>
                      <strong>Starting Player:</strong>
                    </label>
                    <div className="player-options" style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className={`player-option ${startingPlayer === 'white' ? 'active' : ''}`}
                        onClick={() => setStartingPlayer('white')}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          border: `2px solid ${startingPlayer === 'white' ? '#3498db' : '#34495e'}`,
                          borderRadius: '5px',
                          background: startingPlayer === 'white' ? 'rgba(52, 152, 219, 0.2)' : 'transparent',
                          color: '#000',
                          cursor: 'pointer'
                        }}
                      >
                        White
                      </button>
                      <button
                        className={`player-option ${startingPlayer === 'black' ? 'active' : ''}`}
                        onClick={() => setStartingPlayer('black')}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          border: `2px solid ${startingPlayer === 'black' ? '#3498db' : '#34495e'}`,
                          borderRadius: '5px',
                          background: startingPlayer === 'black' ? 'rgba(52, 152, 219, 0.2)' : 'transparent',
                          color: '#000',
                          cursor: 'pointer'
                        }}
                      >
                        Black
                      </button>
                    </div>
                  </div>
                  
                  {/* Saved Positions (Members Only) */}
                  {isMember && (
                    <div className="saved-positions-section" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border-color, #34495e)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button
                          className="btn btn-small"
                          onClick={() => {
                            loadSavedPositions();
                            setShowLoadModal(true);
                          }}
                          style={{ width: '100%' }}
                          disabled={loadingPositions}
                        >
                          📋 Load Saved Position {savedPositions.length > 0 && `(${savedPositions.length})`}
                        </button>
                        <button
                          className="btn btn-small"
                          onClick={() => setShowSaveModal(true)}
                          style={{ width: '100%' }}
                        >
                          💾 Save Current Position
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Import Position (available to all users) */}
                  <div style={{ marginTop: isMember ? '8px' : '20px', paddingTop: isMember ? '0' : '20px', borderTop: isMember ? 'none' : '1px solid var(--border-color, #34495e)' }}>
                    <button
                      className="btn btn-small"
                      onClick={() => setShowImportModal(true)}
                      style={{ width: '100%' }}
                    >
                      📥 Import Position / Moves
                    </button>
                  </div>

                  {showEngineToggle && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '6px', border: '1px solid var(--border-color, #333)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
                        <input type="checkbox" checked={engineEnabled} onChange={(e) => setEngineEnabled(e.target.checked)} />
                        Play vs Engine
                      </label>
                      {engineEnabled && (
                        <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                          <span style={{ color: '#999' }}>Engine plays:</span>
                          <select
                            value={engineColor}
                            onChange={(e) => setEngineColor(e.target.value as 'white' | 'black')}
                            style={{ padding: '2px 4px', fontSize: '11px', backgroundColor: 'var(--bg-color, #1a1a1a)', color: '#fff', border: '1px solid #333', borderRadius: '3px' }}
                          >
                            <option value="black">Black</option>
                            <option value="white">White</option>
                          </select>
                          <span style={{ color: '#666', fontSize: '10px' }}>10s / move</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', marginTop: showEngineToggle ? '10px' : '20px' }}>
                    <button className="btn btn-secondary" onClick={handleClearBoard} style={{ flex: 1 }}>
                      Clear Board
                    </button>
                    <button className="btn btn-primary" onClick={handleStartGame} style={{ flex: 1 }}>
                      {engineEnabled ? 'Start vs Engine' : 'Start Game'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Game started: Move History and Game Controls */}
              <div className="game-controls-area" style={{ height: 'calc(var(--board-size) + 46px)', width: '256px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {gameState && (
                  <>
                    {/* Move History Container - Always visible when game started */}
                    <div id="game-log-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      <div className="review-button-container" style={{ width: '236px', margin: '15px auto 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2 style={{ fontSize: '1.2em', margin: '0' }}>Move History</h2>
                      </div>
                      <div 
                        ref={moveHistoryScrollRef}
                        className="game-log" 
                        id="game-log" 
                        style={{ flex: 1, minHeight: 0 }}
                        onScroll={handleMoveHistoryScroll}
                        onMouseLeave={() => setHistoryHoverIndex(null)}
                      >
                        {moveHistory.length > 0 ? (
                          Array.from({ 
                            length: startingPlayer === 'black' 
                              ? Math.ceil((moveHistory.length + 1) / 2)  // Add 1 to account for the "--" pair
                              : Math.ceil(moveHistory.length / 2) 
                          }, (_, pairIndex) => {
                            // Adjust move indexing based on starting player
                            let whiteMove: MoveHistoryEntry | undefined;
                            let blackMove: MoveHistoryEntry | undefined;
                            let whiteMoveIndex: number;
                            let blackMoveIndex: number;
                            
                            if (startingPlayer === 'black' && pairIndex === 0) {
                              // First pair when black starts: show "--" for white, black's first move
                              whiteMove = undefined;
                              blackMove = moveHistory[0];
                              whiteMoveIndex = -1; // No white move
                              blackMoveIndex = 0;
                            } else if (startingPlayer === 'black') {
                              // Subsequent pairs when black starts: offset by 1
                              whiteMove = moveHistory[pairIndex * 2 - 1];
                              blackMove = moveHistory[pairIndex * 2];
                              whiteMoveIndex = pairIndex * 2 - 1;
                              blackMoveIndex = pairIndex * 2;
                            } else {
                              // Normal case: white starts first
                              whiteMove = moveHistory[pairIndex * 2];
                              blackMove = moveHistory[pairIndex * 2 + 1];
                              whiteMoveIndex = pairIndex * 2;
                              blackMoveIndex = pairIndex * 2 + 1;
                            }
                            
                            const moveNumber = pairIndex + 1;
                            
                            // historyIndex represents position: -1 = initial, 0 = after move 0, etc.
                            // Adjust highlighting logic for black starting first
                            let isWhiteActive = false;
                            let isBlackActive = false;
                            
                            if (isReviewMode) {
                              if (startingPlayer === 'black' && pairIndex === 0) {
                                // First pair when black starts: highlight black move at index 0
                                isBlackActive = historyIndex === 0;
                              } else if (startingPlayer === 'black') {
                                // Subsequent pairs: adjust indices
                                isWhiteActive = whiteMove !== undefined && historyIndex === whiteMoveIndex;
                                isBlackActive = blackMove !== undefined && historyIndex === blackMoveIndex;
                              } else {
                                // Normal case
                                isWhiteActive = whiteMove !== undefined && historyIndex === whiteMoveIndex;
                                isBlackActive = blackMove !== undefined && historyIndex === blackMoveIndex;
                              }
                            }
                            
                            const isActive = isWhiteActive || isBlackActive;
                            
                            return (
                              <div 
                                key={pairIndex} 
                                ref={isActive ? activeReviewMoveRef : null}
                                className="log-entry"
                              >
                                <span className="move-number">{moveNumber}.</span>
                                <span 
                                  className={`white-move ${isWhiteActive ? 'highlighted-move' : ''} ${historyHoverIndex === whiteMoveIndex ? 'history-hover-move' : ''} ${!whiteMove && startingPlayer === 'black' && pairIndex === 0 ? 'no-move' : ''}`}
                                  onClick={() => {
                                    if (isReviewMode && whiteMove) {
                                      goToMove(whiteMoveIndex);
                                    }
                                  }}
                                  onMouseEnter={() => {
                                    if (whiteMove) setHistoryHoverIndex(whiteMoveIndex);
                                  }}
                                  onMouseLeave={() => setHistoryHoverIndex(null)}
                                  style={{ cursor: isReviewMode && whiteMove ? 'pointer' : 'default' }}
                                >
                                  {startingPlayer === 'black' && pairIndex === 0 && !whiteMove ? (
                                    <span style={{ color: '#999', fontStyle: 'italic' }}>--</span>
                                  ) : whiteMove ? (
                                    <span>
                                      {getNotation(whiteMove.col, whiteMove.row)}
                                      {whiteMove.yugos > 0 && <span className="yugo-indicator">{'*'.repeat(whiteMove.yugos)}</span>}
                                    </span>
                                  ) : ''}
                                </span>
                                <span 
                                  className={`black-move ${isBlackActive ? 'highlighted-move' : ''} ${historyHoverIndex === blackMoveIndex ? 'history-hover-move' : ''}`}
                                  onClick={() => {
                                    if (isReviewMode && blackMove) {
                                      goToMove(blackMoveIndex);
                                    }
                                  }}
                                  onMouseEnter={() => {
                                    if (blackMove) setHistoryHoverIndex(blackMoveIndex);
                                  }}
                                  onMouseLeave={() => setHistoryHoverIndex(null)}
                                  style={{ cursor: isReviewMode && blackMove ? 'pointer' : 'default' }}
                                >
                                  {blackMove ? (
                                    <span>
                                      {getNotation(blackMove.col, blackMove.row)}
                                      {blackMove.yugos > 0 && <span className="yugo-indicator">{'*'.repeat(blackMove.yugos)}</span>}
                                    </span>
                                  ) : ''}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                            No moves yet
                          </div>
                        )}
                      </div>
                      
                      {/* Position String Display - At bottom of move history container */}
                      <div className="sandbox-position-string" style={{ marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                        <h4 style={{ marginBottom: '10px', fontSize: '0.9rem' }}>Position String</h4>
                        <div className="position-string-display" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            readOnly
                            value={positionString || (isGameStarted && gameState 
                              ? boardToPositionString(gameState.board, startingPlayer)
                              : boardToPositionString(setupBoard, startingPlayer))}
                            className="position-string-input"
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                            style={{ flex: 1, padding: '6px', fontSize: '0.85rem' }}
                          />
                          <button
                            className="btn btn-small"
                            onClick={handleCopyPositionString}
                            title="Copy position string"
                          >
                            Copy
                          </button>
                        </div>
                        <p className="position-string-help" style={{ fontSize: '0.75rem', color: '#999', margin: 0 }}>
                          Format: WM=White Migo, WY=White Yugo, BM=Black Migo, BY=Black Yugo, START=Starting player
                        </p>
                      </div>
                      
                      {/* Review mode controls - Same as game board */}
                      {isReviewMode && (
                        <div id="review-section" style={{ marginTop: '10px' }}>
                          <div className="review-controls">
                            <button 
                              className="btn" 
                              id="first-move-btn"
                              onClick={firstMove}
                              disabled={historyIndex <= -1}
                              title="First Move"
                            >
                              <span className="arrow-icon">⏮</span>
                            </button>
                            <button 
                              className="btn" 
                              id="prev-move-btn"
                              onMouseDown={(e) => startHoldScroll('prev', e)}
                              onMouseUp={(e) => stopHoldScroll('prev', e)}
                              onMouseLeave={(e) => stopHoldScroll('prev', e)}
                              onTouchStart={(e) => startHoldScroll('prev', e)}
                              onTouchEnd={(e) => stopHoldScroll('prev', e)}
                              disabled={historyIndex <= -1}
                              title="Previous Move (Hold to scroll)"
                            >
                              <span className="arrow-icon">◀</span>
                            </button>
                            <button 
                              className="btn" 
                              id="next-move-btn"
                              onMouseDown={(e) => startHoldScroll('next', e)}
                              onMouseUp={(e) => stopHoldScroll('next', e)}
                              onMouseLeave={(e) => stopHoldScroll('next', e)}
                              onTouchStart={(e) => startHoldScroll('next', e)}
                              onTouchEnd={(e) => stopHoldScroll('next', e)}
                              disabled={historyIndex >= moveHistory.length}
                              title="Next Move (Hold to scroll)"
                            >
                              <span className="arrow-icon">▶</span>
                            </button>
                            <button 
                              className="btn" 
                              id="last-move-btn"
                              onClick={lastMove}
                              disabled={historyIndex >= moveHistory.length}
                              title="Last Move"
                            >
                              <span className="arrow-icon">⏭</span>
                            </button>
                          </div>
                          <div style={{ textAlign: 'center', marginTop: '5px' }}>
                            <button 
                              className="btn" 
                              onClick={exitReviewMode}
                              style={{ fontSize: '10px', padding: '2px 8px', height: '20px', lineHeight: '1' }}
                            >
                              Exit Review
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Review and Undo buttons when not in review mode */}
                      {moveHistory.length > 0 && !isReviewMode && (
                        <div style={{ width: '100%', margin: '10px 0 0 0', display: 'flex', gap: '8px', justifyContent: 'center', padding: '0 10px', boxSizing: 'border-box' }}>
                          <button 
                            className="review-button" 
                            onClick={enterReviewMode}
                            style={{ flex: 1, maxWidth: '110px' }}
                          >
                            Review
                          </button>
                          <button
                            className="review-button"
                            onClick={handleUndoMove}
                            title="Undo last move"
                            style={{ flex: 1, maxWidth: '110px' }}
                          >
                            Undo
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Game Controls - Below move history container */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', width: '100%', padding: '0 10px', boxSizing: 'border-box' }}>
                      <button 
                        className="btn btn-secondary" 
                        onClick={handleUndoMove}
                        disabled={moveHistory.length === 0}
                        title="Undo last move"
                        style={{ flex: 1 }}
                      >
                        Undo
                      </button>
                      <button className="btn btn-secondary" onClick={handleClearBoard} style={{ flex: 1 }}>
                        Reset
                      </button>
                      <button className="btn btn-primary" onClick={handleSaveAndGoToGameBoard} style={{ flex: 1 }}>
                        Save & Go
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Save Position Modal */}
      {showSaveModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowSaveModal(false);
            setSavePositionName('');
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
              color: '#000'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Save Position</h3>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.9rem' }}>
              Position Name:
            </label>
            <input
              type="text"
              value={savePositionName}
              onChange={(e) => setSavePositionName(e.target.value)}
              placeholder="Enter a name for this position"
              maxLength={100}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '0.9rem',
                borderRadius: '5px',
                border: '1px solid #34495e',
                marginBottom: '15px',
                color: '#000',
                backgroundColor: '#fff'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSavePosition();
                } else if (e.key === 'Escape') {
                  setShowSaveModal(false);
                  setSavePositionName('');
                }
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSaveModal(false);
                  setSavePositionName('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSavePosition}
                disabled={!savePositionName.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Import Position / Move History Modal */}
      {showImportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowImportModal(false);
            setImportPositionText('');
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              color: '#000'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Import Position or Move History</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>
              Paste a position string or move history below. The format is detected automatically.
            </p>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '10px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontFamily: 'monospace' }}>
              <strong style={{ fontFamily: 'inherit' }}>Move History:</strong><br/>
              1. a1&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;b2<br/>
              2. c3&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;d4*<br/>
              3. e5**&nbsp;&nbsp;&nbsp;b6<br/><br/>
              <strong style={{ fontFamily: 'inherit' }}>Position String:</strong><br/>
              WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white
            </div>
            <textarea
              value={importPositionText}
              onChange={(e) => setImportPositionText(e.target.value)}
              placeholder={"Paste move history or position string here...\n\n1. a1      b2\n2. c3      d4*\n3. e5**    b6\n\nor\n\nWM:a1,b2;BM:c3;START:white"}
              rows={6}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '0.9rem',
                borderRadius: '5px',
                border: '1px solid #34495e',
                marginBottom: '15px',
                color: '#000',
                backgroundColor: '#fff',
                resize: 'vertical',
                fontFamily: 'monospace',
                boxSizing: 'border-box'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setShowImportModal(false);
                  setImportPositionText('');
                }
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportPositionText('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={!importPositionText.trim()}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Saved Positions Modal */}
      {showLoadModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowLoadModal(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              color: '#000'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
              Saved Positions {savedPositions.length > 0 && `(${savedPositions.length})`}
            </h3>
            
            {loadingPositions ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                Loading...
              </div>
            ) : savedPositions.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No saved positions yet. Save a position to get started!
              </div>
            ) : (
              <div style={{ 
                maxHeight: '400px', 
                overflowY: 'auto', 
                marginBottom: '15px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '10px'
              }}>
                {savedPositions.map((pos) => {
                  const date = new Date(pos.updated_at || pos.created_at);
                  const formattedDate = date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  return (
                    <div
                      key={pos.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px',
                        marginBottom: '8px',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#000' }}>
                          {pos.name}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {formattedDate}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginLeft: '10px' }}>
                        <button
                          className="btn btn-small"
                          onClick={() => handleLoadPosition(pos.id)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '0.85rem'
                          }}
                        >
                          Load
                        </button>
                        <button
                          onClick={(e) => handleDeletePosition(pos.id, e)}
                          style={{
                            padding: '6px 10px',
                            fontSize: '0.85rem',
                            backgroundColor: '#e74c3c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Delete position"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowLoadModal(false)}
              >
                Close
              </button>
              {savedPositions.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowLoadModal(false);
                    setShowSaveModal(true);
                  }}
                >
                  Save New Position
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
